/**
 * The overworld — a walkable tile map of the ten kingdoms (docs/rpg.md §22).
 *
 * §22 asks for a map a student can *explore*: towns, forests, mountains,
 * bridges, coast. The list-shaped map built earlier answers §33's question
 * ("where am I, what is open, what is sealed") and none of §22's, so this adds
 * the walking without taking the answers away — `WorldMapPage` draws this map
 * over the kingdom list rather than instead of it.
 *
 * **Walking is not an access control, and terrain is not a lock.** This is the
 * same invariant every other file in this package is written to
 * (`docs/rpg-implementation-plan.md` §0.1), and a tile map is the easiest place
 * in the whole codebase to break it: water and mountains are impassable
 * *scenery*, so the moment one of them separates a kingdom's gate from the
 * road, terrain has become a wall around mathematics. `validateOverworld`
 * therefore walks the map and proves every gate is reachable from the start,
 * and `npm run content:validate` fails if one is not. Sealed kingdoms are
 * walked up to and entered exactly like open ones; what the seal withholds is
 * the story inside, and the kingdom page says so.
 *
 * **The map is data and the walking is arithmetic.** No clock, no randomness,
 * no game loop: a step is a pure function of (map, position, direction) and a
 * path is a breadth-first search with a fixed direction order, so the same
 * click walks the same route on every machine. Even the scenery is derived
 * from the tile's own coordinates — `packages/world` sits below `apps/web` and
 * the no-`Math.random` rule applies here exactly as it does to `Confetti`.
 */

/*
 * One-directional, and it stays that way: `isles.ts` knows nothing about a
 * grid, so the continent may name an isle's landing without the isles module
 * ever having to know what a tile is.
 */
import type { IsleLandmark } from './isles.ts';

/**
 * What a tile is made of.
 *
 * Ten kinds, and the count is deliberate rather than incidental: a legend
 * nobody reads is scenery, so a terrain earns its place only when some map
 * cannot be drawn without it. The first seven are the outdoors, which is the
 * whole of what a continent and a village need. The last three arrived with the
 * region maps (`town.ts`), where the ground is a floor and the coastline is a
 * wall — a cottage drawn out of grass and mountains is a cottage in name only,
 * and the words under the map would have called its hearthside "the hillside".
 */
export type Terrain =
  | 'grass'
  | 'road'
  | 'bridge'
  | 'forest'
  | 'sand'
  | 'water'
  | 'mountain'
  | 'floor'
  | 'wall'
  | 'crop'
  /*
   * The eleventh, and it earned its place the way the legend says one must: the
   * far north could not be drawn without it. A snowfield written as sand is a
   * snowfield the map's own words call "sand" (`TERRAIN_WORD`), and the whole
   * point of North Reach is that a student can see they have walked somewhere
   * cold and far. Walkable, like grass — snow is weather, not a wall.
   */
  | 'snow'
  /*
   * The twelfth, and it is held to the same test the eleventh was: could the
   * map be drawn without it? A desert *looks* like `sand` — but `sand` is the
   * shore, and `TERRAIN_WORD` calls it one. A continent whose beach and whose
   * erg are the same tile cannot tell a student which they are standing in,
   * and the sentence under the map would greet the middle of the desert with
   * "shore". Walkable, like sand: a dune is hard going and not a wall.
   */
  | 'dune';

/**
 * The character each terrain is authored as.
 *
 * A map is written as rows of text in `content/src/world/overworld.ts` because
 * that is the one format an author can *see* while editing: a coastline is
 * obvious in a block of characters and invisible in an array of objects.
 */
export const TERRAIN_CHARS: Readonly<Record<string, Terrain>> = {
  '.': 'grass',
  '#': 'road',
  '=': 'bridge',
  f: 'forest',
  ',': 'sand',
  '~': 'water',
  '^': 'mountain',
  _: 'floor',
  '%': 'wall',
  '"': 'crop',
  '*': 'snow',
  '-': 'dune',
};

/**
 * Which terrain can be walked on.
 *
 * Forest is walkable and drawn dense; it slows nothing down, because a tile map
 * that punished a student for taking the scenic route would be a game mechanic
 * charging rent on curiosity. Water and mountain are the only two that stop a
 * step, and they are why `validateOverworld` exists.
 */
export const WALKABLE: Readonly<Record<Terrain, boolean>> = {
  grass: true,
  road: true,
  bridge: true,
  forest: true,
  sand: true,
  water: false,
  mountain: false,
  floor: true,
  /* The third impassable kind, and the one that needs watching: a wall is how
     a room is drawn, so it is also the easiest way in this codebase to fence a
     concept off by accident. `validateRegionMap` walks every room. */
  wall: false,
  crop: true,
  snow: true,
  dune: true,
};

export type Position = { x: number; y: number };

/**
 * The least a map has to be for the walking below to work on it.
 *
 * The continent is not the only tile map on the site — `Town` in `town.ts`
 * draws the inside of a kingdom on the same squares — and every function in
 * this file except the landmark lookups needs nothing from a map but its
 * ground. Typing them against the grid rather than against `Overworld` is what
 * lets a village be walked by the same breadth-first search the continent is,
 * with no second copy of it to keep in step.
 */
export type TileGrid = {
  width: number;
  height: number;
  /** One string per row, one character per tile, keyed by `TERRAIN_CHARS`. */
  rows: readonly string[];
};

export type Direction = 'north' | 'south' | 'east' | 'west';

/**
 * The four steps, in the order every search here considers them.
 *
 * Fixed, and load-bearing: `pathBetween` breaks ties by this order, so a route
 * around a lake is the same route for every student on every device. A `Set`
 * iteration order or a sort by distance would both be "correct" and neither
 * would be reproducible in a test.
 */
export const STEPS: Readonly<Record<Direction, Position>> = {
  north: { x: 0, y: -1 },
  east: { x: 1, y: 0 },
  south: { x: 0, y: 1 },
  west: { x: -1, y: 0 },
};

const ORDER: Direction[] = ['north', 'east', 'south', 'west'];

/** A kingdom's gate: the tile a student walks onto to enter it. */
export type Landmark = {
  kingdomId: string;
  x: number;
  y: number;
};

export type Overworld = TileGrid & {
  /** Where a student with no kingdom of their own begins. */
  start: Position;
  landmarks: readonly Landmark[];
  /**
   * The landings of the offshore isles (`isles.ts`), if the map has any.
   *
   * Separate from `landmarks` rather than a variant of it, because the two are
   * not the same kind of thing and the difference is the one that matters here:
   * a kingdom's gate must always be reachable and always opens, and an isle's
   * landing may be shut until an achievement is earned. Keeping them in one
   * list would put a status on the type that a kingdom gate must never have.
   */
  isles?: readonly IsleLandmark[];
};

/* -------------------------------------------------------------------------- */
/* Reading the map                                                            */
/* -------------------------------------------------------------------------- */

export const samePosition = (a: Position, b: Position): boolean => a.x === b.x && a.y === b.y;

/** `"12,7"` — a position as a Map/Set key. */
export const positionKey = (position: Position): string => `${position.x},${position.y}`;

export function inBounds(world: TileGrid, position: Position): boolean {
  return position.x >= 0 && position.y >= 0 && position.x < world.width && position.y < world.height;
}

/**
 * What is at a tile, or `undefined` off the edge of the world.
 *
 * An unknown character reads as `water` rather than throwing: a typo in a map
 * row should make a hole a student cannot walk into — which `validateOverworld`
 * then reports by name — rather than an exception on a page that was working a
 * moment ago.
 */
export function terrainAt(world: TileGrid, position: Position): Terrain | undefined {
  if (!inBounds(world, position)) return undefined;
  const char = world.rows[position.y]?.[position.x];
  if (char === undefined) return undefined;
  return TERRAIN_CHARS[char] ?? 'water';
}

export function isWalkable(world: TileGrid, position: Position): boolean {
  const terrain = terrainAt(world, position);
  return terrain !== undefined && WALKABLE[terrain];
}

/**
 * One step, or exactly where you were.
 *
 * Refusing to move is the whole of what impassable terrain does — there is no
 * message, no penalty and no sound. A student who walks into the sea stays on
 * the sand, which is what every tile map has always done and needs no
 * explaining.
 */
export function stepFrom(world: TileGrid, from: Position, direction: Direction): Position {
  const delta = STEPS[direction];
  const next = { x: from.x + delta.x, y: from.y + delta.y };
  return isWalkable(world, next) ? next : from;
}

export const landmarkAt = (world: Overworld, position: Position): Landmark | undefined =>
  world.landmarks.find((landmark) => landmark.x === position.x && landmark.y === position.y);

export const landmarkOf = (world: Overworld, kingdomId: string): Landmark | undefined =>
  world.landmarks.find((landmark) => landmark.kingdomId === kingdomId);

/** Where a student standing in this kingdom should be put down. */
export function gateOf(world: Overworld, kingdomId: string | undefined): Position {
  const landmark = kingdomId === undefined ? undefined : landmarkOf(world, kingdomId);
  return landmark ? { x: landmark.x, y: landmark.y } : world.start;
}

/**
 * The nearest gate within `radius` tiles, by walking distance rather than by
 * how it looks: a kingdom across a bay is not nearby, however close it is.
 *
 * This is what the live region on the map page announces, so a student who
 * cannot see the tiles still hears "Starter Village is two steps east".
 */
export function nearestLandmark(
  world: Overworld,
  from: Position,
  radius = 6,
): { landmark: Landmark; steps: number } | undefined {
  const distances = walkingDistances(world, from, radius);
  let best: { landmark: Landmark; steps: number } | undefined;
  for (const landmark of world.landmarks) {
    const steps = distances.get(positionKey(landmark));
    if (steps === undefined) continue;
    if (!best || steps < best.steps) best = { landmark, steps };
  }
  return best;
}

/** Which way `to` lies from `from`, in words a sentence can use. */
export function bearing(from: Position, to: Position): string {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const parts: string[] = [];
  if (dy < 0) parts.push('north');
  if (dy > 0) parts.push('south');
  if (dx > 0) parts.push('east');
  if (dx < 0) parts.push('west');
  return parts.length === 0 ? 'here' : parts.join('-');
}

/* -------------------------------------------------------------------------- */
/* Walking                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * How many steps every reachable tile is from `from`, out to `limit` steps.
 *
 * The one search this file has; `pathBetween`, `nearestLandmark` and
 * `reachableFrom` are all it wearing different hats.
 */
export function walkingDistances(world: TileGrid, from: Position, limit = Infinity): Map<string, number> {
  const distances = new Map<string, number>();
  if (!isWalkable(world, from)) return distances;

  distances.set(positionKey(from), 0);
  let frontier: Position[] = [from];

  while (frontier.length > 0) {
    const next: Position[] = [];
    for (const position of frontier) {
      const steps = distances.get(positionKey(position)) ?? 0;
      if (steps >= limit) continue;
      for (const direction of ORDER) {
        const delta = STEPS[direction];
        const candidate = { x: position.x + delta.x, y: position.y + delta.y };
        const key = positionKey(candidate);
        if (distances.has(key) || !isWalkable(world, candidate)) continue;
        distances.set(key, steps + 1);
        next.push(candidate);
      }
    }
    frontier = next;
  }
  return distances;
}

/** Every tile that can be walked to from here. The reachability proof's engine. */
export const reachableFrom = (world: TileGrid, from: Position): Set<string> =>
  new Set(walkingDistances(world, from).keys());

/**
 * The shortest walk from one tile to another, excluding the tile stood on and
 * including the destination. Empty when there is no way there — which is the
 * honest answer for the far side of a lake, and never the answer for a kingdom.
 *
 * `detour` marks tiles the walk would rather not cross. It exists for one
 * thing: stepping onto a signpost takes you where it points, so a route that
 * happened to run over one would carry a student into a kingdom they were only
 * walking past. The destination is never detoured around — going *to* a
 * signpost is the whole point — and if avoiding them leaves no way through at
 * all, the walk takes the direct route rather than refusing to move. Being
 * carried somewhere you did not mean to go is a smaller failure than a map that
 * will not walk.
 */
export function pathBetween(
  world: TileGrid,
  from: Position,
  to: Position,
  detour?: (position: Position) => boolean,
): Position[] {
  if (samePosition(from, to) || !isWalkable(world, to) || !isWalkable(world, from)) return [];

  const cameFrom = new Map<string, Position>();
  const seen = new Set<string>([positionKey(from)]);
  let frontier: Position[] = [from];
  let found = false;

  while (frontier.length > 0 && !found) {
    const next: Position[] = [];
    for (const position of frontier) {
      for (const direction of ORDER) {
        const delta = STEPS[direction];
        const candidate = { x: position.x + delta.x, y: position.y + delta.y };
        const key = positionKey(candidate);
        if (seen.has(key) || !isWalkable(world, candidate)) continue;
        if (detour && detour(candidate) && !samePosition(candidate, to)) continue;
        seen.add(key);
        cameFrom.set(key, position);
        if (samePosition(candidate, to)) {
          found = true;
          break;
        }
        next.push(candidate);
      }
      if (found) break;
    }
    frontier = next;
  }

  if (!found) return detour ? pathBetween(world, from, to) : [];

  const path: Position[] = [];
  let cursor: Position | undefined = to;
  while (cursor && !samePosition(cursor, from)) {
    path.unshift(cursor);
    cursor = cameFrom.get(positionKey(cursor));
  }
  return path;
}

/* -------------------------------------------------------------------------- */
/* Scenery                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Which variant of a tile's decoration to draw, derived from where the tile is.
 *
 * A forest of identical trees looks like wallpaper and a forest of random ones
 * cannot be tested — and re-randomises under the student on every re-render,
 * which is the same bug `Confetti` was written to avoid. So the variant is a
 * small hash of the coordinates: varied across the map, identical on every
 * machine, and stable for as long as the tile is where it is.
 */
export function sceneryVariant(position: Position, variants: number): number {
  if (variants <= 1) return 0;
  const mixed = (position.x * 73_856_093) ^ (position.y * 19_349_663);
  return Math.abs(mixed) % variants;
}
